import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@breathelife/mui';
import {
  ESignCeremony,
  ESignCeremonyStatus,
  ESignSigner,
  ESignSignerAuthMethod,
  ESignSignerStatus,
  Permission,
} from '@breathelife/types';

import { Button } from '../../../../Button/Button';
import { PopupMenu, MenuAction } from '../../../../PopupMenu/PopupMenu';
import {
  useResendEmailMutation,
  useResendSmsMutation,
  useUnlockSignerMutation,
} from '../../../../../ReactQuery/ESignCeremony/eSignSignerActions.mutations';
import { useCarrierContext, useSelector } from '../../../../../Hooks';
import { userHasPermission } from '../../../../../Helpers/user';

// Statuses where the OneSpan package is still actionable (sent but not yet completed/cancelled).
const ACTIVE_CEREMONY_STATUSES: ESignCeremonyStatus[] = [
  ESignCeremonyStatus.SENT,
  ESignCeremonyStatus.SIGNER_LOCKED_OUT,
  ESignCeremonyStatus.EMAIL_BOUNCED,
  ESignCeremonyStatus.OPT_OUT,
  ESignCeremonyStatus.DECLINED,
  ESignCeremonyStatus.EXPIRED,
  ESignCeremonyStatus.SEND_PACKAGE_ERROR,
];

type SignerActionsMenuProps = {
  signer: ESignSigner;
  applicationId: string;
  eSignCeremony: ESignCeremony | null | undefined;
  onEditSigner: (signer: ESignSigner) => void;
};

export function SignerActionsMenu(props: SignerActionsMenuProps): ReactElement | null {
  const { signer, applicationId, eSignCeremony, onEditSigner } = props;
  const { t } = useTranslation();
  const { features } = useCarrierContext();
  const user = useSelector((state) => state.leadPlatform.authentication.user);

  const isEditCeremonyEnabled = features.eSignature.editCeremony;
  const canEditCeremony = userHasPermission(user?.permissions, [Permission.ESignCeremonyEdit]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const unlockMutation = useUnlockSignerMutation({ onSuccess: () => setAnchorEl(null) });
  const resendEmailMutation = useResendEmailMutation({ onSuccess: () => setAnchorEl(null) });
  const resendSmsMutation = useResendSmsMutation({ onSuccess: () => setAnchorEl(null) });

  if (!isEditCeremonyEnabled || !canEditCeremony) return null;

  // No ceremony — button not present.
  if (!eSignCeremony) return null;

  const isActive = ACTIVE_CEREMONY_STATUSES.includes(eSignCeremony.status);
  const isCompleted = eSignCeremony.status === ESignCeremonyStatus.COMPLETED;

  // Ceremony exists but is in a non-actionable state — show a disabled button with tooltip.
  if (!isActive && !isCompleted) {
    return (
      <Tooltip title={t('assistedApplication.signerActions.actionsDisabledTooltip')}>
        <span>
          <Button variant='contained' size='small' disabled>
            {t('assistedApplication.signerActions.actionsMenu')}
          </Button>
        </span>
      </Tooltip>
    );
  }

  const isSigned = signer.status === ESignSignerStatus.SIGNED;
  const isLocked = signer.status === ESignSignerStatus.SIGNER_LOCKED_OUT;
  const isCellphoneAuth = signer.authMethod === ESignSignerAuthMethod.cellphone;
  const isLoading = unlockMutation.isLoading || resendEmailMutation.isLoading || resendSmsMutation.isLoading;

  // Resend email: available when the ceremony is completed (both remote and in-person),
  // or when a remote ceremony is active and the signer is not locked.
  // Not shown for active in-person ceremonies — there is no signing-link email to resend.
  const showResendEmail = (!isLocked && isActive && !eSignCeremony.inPerson) || isCompleted;

  const actions: MenuAction[] = [
    ...((isActive || isCompleted) && isLocked
      ? [
          {
            tag: 'MenuAction' as const,
            label: t('assistedApplication.signerActions.unlock'),
            disabled: isLoading,
            onClick: () => unlockMutation.mutate({ signerId: signer.id, applicationId }),
          },
        ]
      : []),
    ...(showResendEmail
      ? [
          {
            tag: 'MenuAction' as const,
            label: t('assistedApplication.signerActions.resendEmail'),
            disabled: isLoading,
            onClick: () => resendEmailMutation.mutate({ signerId: signer.id, applicationId }),
          },
        ]
      : []),
    ...(isActive && !isLocked && !isSigned && isCellphoneAuth
      ? [
          {
            tag: 'MenuAction' as const,
            label: t('assistedApplication.signerActions.resendSms'),
            disabled: isLoading,
            onClick: () => resendSmsMutation.mutate({ signerId: signer.id, applicationId }),
          },
        ]
      : []),
    ...(isActive && !isSigned
      ? [
          {
            tag: 'MenuAction' as const,
            label: t('assistedApplication.signerActions.editSigner'),
            disabled: isLoading,
            onClick: () => {
              setAnchorEl(null);
              onEditSigner(signer);
            },
          },
        ]
      : []),
  ];

  if (actions.length === 0) return null;

  return (
    <>
      <Button variant='contained' size='small' onClick={(e) => setAnchorEl(e.currentTarget)} aria-haspopup='true'>
        {t('assistedApplication.signerActions.actionsMenu')}
      </Button>
      <PopupMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        items={actions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </>
  );
}
